// String-based state management for answered questions
// so that it can be put into the URL.

import { differenceInYears } from 'date-fns';
import { prop, trimLowerCase } from '../../utils/utils';
import { updateInStorage, getObjectFromStorage, clearFromStorage } from '../../utils/get-location/location-from-storage';
import { getWorkflow, computeRisk, raiseGAEvent } from '../../utils/network';
import { parseQueryString, appStorage } from '../../utils/utils';
import getSource from '../../utils/get-source';
import {
  ess
} from './ess';
import { fireSurveyResultEvent } from '../../utils/events';
import config from '../../config.json'

export const stateStringToObject = state => (state.length === 0
  ? {}
  : state.split(',').reduce(
    (acc, pair) => ({ ...acc, [pair.split(':')[0]]: pair.split(':')[1] }),
    {}
  ));

const objectToStateString = obj => Object.entries(obj)
  .map(([key, value]) => `${key}:${value}`)
  .join(',');

export const setAnswer = (questionId, answerId, stateString = '') => (
  objectToStateString({ ...stateStringToObject(stateString), [questionId]: answerId })
);

export const questions = prop('worflow_template');
export const answers = prop('options');
export const multiListanswers = prop('familyOptions');
export const questionId = prop('question_id');
export const questionName = prop('name');
export const answerId = prop('id');

export const formatAnswers = selectedAnswers => {
  if (Array.isArray(selectedAnswers.current)) {
    return [selectedAnswers.current[1]];
  }
  if (Array.isArray(selectedAnswers)) {
    return selectedAnswers.join(';');
  }
  return answerId(selectedAnswers);
};

export const isQuestionOfId = qId => question => questionId(question) === qId;

export const isQuestionOfName = name => question => questionName(question) === name;

export const questionById = (id, questions) => questions.find(isQuestionOfId(id));

export const questionByName = (name, questions) => questions.find(isQuestionOfName(name));

const getAnswersById = (answerId, answerIds) => (answerIds.split(';').includes(answerId));

const getAnswersByCommaSeperatedId = (answerId, answerIds) => (answerIds.split(',').includes(answerId));

export const isLocationQuestion = question => (question.type === 'custom' && question.q_id === 'cq1');

export const isProfileSelectionQuestion = question => (question.type === 'custom' && question.q_id === 'cq2');

export const isMultiSelectQuestion = question => trimLowerCase(question.selection_type) === 'multi-select';

export const isSingleListMultiList = question => trimLowerCase(question.selection_type) === 'single-select-multi-list';

export const isUserInputNumberQuestion = question => trimLowerCase(question.selection_type) === 'number';

export const isUserInputTextQuestion = question => trimLowerCase(question.selection_type) === 'text';

export const isAudioInputQuestion = question => trimLowerCase(question.selection_type) === 'audio';


const getOptionsFromStorage =
  /* istanbul ignore next */
  (matchingQuestion, aId) => {
    if (isUserInputNumberQuestion(matchingQuestion)) return [parseInt(aId)];
    if (isAudioInputQuestion(matchingQuestion)) return aId.split(',');
    return answers(matchingQuestion).filter(answer => getAnswersByCommaSeperatedId(answerId(answer), aId));
  };

export const responseForPosting =
  /* istanbul ignore next */
  (questions) => {
    const stateObject = [];
    questions.forEach(question => {
      if (question && questionId(question) && getObjectFromStorage(questionId(question))) {
        stateObject.push({ qId: questionId(question), aId: getObjectFromStorage(questionId(question)) })
      }
    });
    const workflowResponses = stateObject.map(({ qId, aId }) => {

      let matchingQuestion = {};
      if (qId === 'cq2') {
        // relation question
        matchingQuestion = questionByName('relation', questions);
      } else {
        matchingQuestion = questionById(qId, questions);
      }
      return {
        ...matchingQuestion,
        options: getOptionsFromStorage(matchingQuestion, aId)
      };
    }).filter(x => x !== false);
    return workflowResponses;
  }

export const nextQuestionId = (currentQuestionId, questions) => {
  // console.log("a,b,c,".split(','));
  // check for null currentQuestionId
  const currentQuestionIndex = questions.findIndex(isQuestionOfId(currentQuestionId));
  // check if currentQuestionIndex is end of list
  if (currentQuestionIndex >= questions.length) {
    return null;// TODO: what?
  }
  // increment the question index
  let nextQuestionIndex = currentQuestionIndex + 1;
  // retreive the next question

  while (nextQuestionIndex < questions.length) {
    const nextQuestion = questions[nextQuestionIndex];
    // check if we have to display this question or not
    let numberOfDependency = 0;
    if (Array.isArray(nextQuestion.dependencies)) {
      numberOfDependency = nextQuestion.dependencies.length;
    }
    if (numberOfDependency === 0) {
      return questions[nextQuestionIndex].question_id;
    }
    let conditionMatch = true;
    for (let i = 0; i < numberOfDependency; i += 1) {
      const dependency = nextQuestion.dependencies[i];
      const selectedOptionId = getObjectFromStorage(dependency.question);
      if (dependency.condition === 'present') {
        if (selectedOptionId && !selectedOptionId.includes(dependency.options[0])) {
          conditionMatch = false;
          break;
        }
      } else if (dependency.condition === 'range') {
        if (selectedOptionId > dependency.range.max || selectedOptionId < dependency.range.min) {
          conditionMatch = false;
          break;
        }
      } else if (dependency.condition === 'absent') {
        if (selectedOptionId && selectedOptionId.includes(dependency.options[0])) {
          conditionMatch = false;
          break;
        }
      }
    }
    // if conditions match = false, then skip this question
    if (conditionMatch) {
      return questions[nextQuestionIndex].question_id;
    }
    nextQuestionIndex += 1;
  }
};

const getQuestionByName = (name, questions) => questions.find(q => q.name === name);
const getAnswerByName = (name, question) => answers(question).find(
  answer => trimLowerCase(answer.name) === trimLowerCase(name)
);
const getAnswerByNameMultiList = (name, question) => multiListanswers(question).find(
  answer => trimLowerCase(answer.name) === trimLowerCase(name)
);

const answerQuestion =
  /* istanbul ignore next */
  (questionName, answerName, stateString, questions) => {
    if (questionName === 'age') { questionName = 'enter age'; }
    const q = getQuestionByName(questionName, questions);
    if (questionName === 'enter age') {
      updateInStorage(questionId(q), answerName);
      return setAnswer(questionId(q), answerName, stateString);
    }
    let a = getAnswerByName(answerName, q);
    if (!a) {
      a = getAnswerByNameMultiList(answerName, q);
    }
    updateInStorage(questionId(q), answerId(a));
    return setAnswer(questionId(q), answerId(a), stateString);
  };

export const getAge = dob => differenceInYears(new Date(), new Date(dob));

export const ageToAgeGroup = age => {
  if (age < 12) return 'Less than 12 years';
  if (age < 50) return '12 - 50 years';
  if (age < 60) return '51 - 60 years';
  return 'Above 60 years';
};

const empExMap = {
  self: 'For yourself',
  myself: 'For yourself',
  spouse: 'Spouse',
  'divorced spouse': 'Someone else',
  father: 'Parent',
  mother: 'Parent',
  'registered Partner': 'Someone else',
  child: 'Child',
  son: 'Child',
  daughter: 'Child',
  guardian: 'Someone else',
  stepchild: 'Child',
  brother: 'Someone else',
  sister: 'Someone else',
  'father in - law': 'Someone else',
  'mother in - law': 'Someone else',
  'father-in-law': 'Someone else',
  'mother-in-law': 'Someone else',
  male: 'Male',
  female: 'Female',
  other: 'Other'
};

export const relationMap =
  /* istanbul ignore next */
  (relation, questions) => {
    const question = getQuestionByName('relation', questions);
    const mappedRelation = trimLowerCase(empExMap[relation]);
    if (mappedRelation === 'foryourself') {
      return mappedRelation;
    }
    const result = question.options.filter(ans => (trimLowerCase(ans.name) === relation));
    return result.length > 0 ? result[0].name : 'Someone else';
  };

export const addLocationQuestion = (questionText, questions) => [
  ...questions.slice(0, 3),
  ...questions.slice(3)
  // locationQuestion
];

const addOptionIfNotExists =
  /* istanbul ignore next */
  (list, option) => {
    let exists = false;
    list.some(listItem => {
      if (listItem.name === option.name) {
        exists = true;
        return true;
      }
      return false;
    });
    if (!exists) {
      list.push(option);
    }
    return list;
  };

const moveArrayItems =
  /* istanbul ignore next */
  (arr, moveTo, val) => {
    const moveFrom = arr.findIndex(arrItem => arrItem.profile_relation === val);
    if (moveFrom >= 0) { arr.splice(moveTo, 0, arr.splice(moveFrom, 1)[0]); }
    return arr;
  };

const buildProfileDataOptions =
  /* istanbul ignore next */
  userData => {
    let { family_details: familyDetails = [] } = userData;
    const newObj = { ...userData };
    delete newObj.family_details;
    const selfDetail = newObj;
    let exists = false;
    // TODO fix this HACK
    familyDetails.forEach(familyMember => {
      if (familyMember.guru_user_id === selfDetail.guru_user_id) {
        exists = true;
      }
    });
    !exists && familyDetails.push(selfDetail);
    familyDetails = familyDetails.map(familyMember => {
      familyMember.id = familyMember.guru_user_id;
      familyMember.preferred_term = `${familyMember.first_name} ${familyMember.last_name}`;
      familyMember.profile_relation = familyMember.primary_guru_user_relation ? `${familyMember.primary_guru_user_relation}` : 'self';
      return familyMember;
    });
    familyDetails = moveArrayItems(familyDetails, 0, 'self');
    familyDetails = moveArrayItems(familyDetails, 1, 'spouse');
    return familyDetails;
  };

export const addlProfileQuestion =
  /* istanbul ignore next */
  (questionText, questions, userData = {}) => {
    const options = buildProfileDataOptions(userData);
    if (options.length === 0) {
      return questions;
    }
    const defaultFamilyMembers = JSON.parse(JSON.stringify(questions[0]));
    const constantOption = defaultFamilyMembers.options.filter(optionItem => (trimLowerCase(optionItem.name) === 'someoneelse'))[0];
    options.forEach((profileOpt, pindex) => {
      const profileRelation = trimLowerCase(profileOpt.primary_guru_user_relation || 'self');
      const relationToMap = relationMap(trimLowerCase(profileRelation), questions);
      defaultFamilyMembers.options.forEach((opt, index) => {
        let updatedOption = {};
        if (relationToMap === trimLowerCase(opt.name)) {
          if ((relationToMap === 'spouse' || relationToMap === 'foryourself')) {
            updatedOption = defaultFamilyMembers.options.splice(index, 1);
          } else {
            updatedOption = [defaultFamilyMembers.options[index]];
          }
          if (opt && opt.length > 0) {
            options[pindex] = { ...updatedOption[0], ...profileOpt };
          }
        }
      });
    });
    /* istanbul ignore next */
    defaultFamilyMembers.options = addOptionIfNotExists(defaultFamilyMembers.options, constantOption);
    const profileQuestion = {
      type: 'custom',
      question_id: 'cq2',
      question_text: questionText,
      name: 'profile_selection',
      selection_type: 'single-select-multi-list',
      option_type: '',
      options: defaultFamilyMembers.options,
      familyOptions: options
    };
    return [
      profileQuestion,
      ...questions.slice(1)
    ];
  };



// eslint-disable-next-line max-len
// source_id=emp_xp&business_id=JIO&source_user_id=55017060&latitude=234&longitude=123&source_relation=Father&source_user_relation_id=null&source_age_years=52&source_gender=Male&place=jamnagar
export const preFillInitialQuestions =
  /* istanbul ignore next */
  (questions, data) => {
    const s1 = answerQuestion(
      'relation',
      relationMap(trimLowerCase(data.relation), questions),
      '',
      questions
    );

    const s2 = answerQuestion(
      'gender',
      data.gender,
      s1,
      questions
    );
    //const workflow_id = getObjectFromStorage('workflow_id') ? getObjectFromStorage('workflow_id') : 'covid_survey_v3';
    const workflow_id = getObjectFromStorage('workflow_id');
    return answerQuestion(
      'enter age',
      parseInt(data.age, 10),
      s2,
      questions
      /*) : answerQuestion(
        'age-group',
        ageToAgeGroup(data.age),
        s2,
        questions*/
    );
  };

export const getUserId =
  /* istanbul ignore next */
  async () => {
    const { uid } = parseQueryString();
    if (uid)
      return Promise.resolve(uid);
    const sourceData = await getSource();
    return sourceData.source_user_id;
  }

export const getUserIdJhh =
  /* istanbul ignore next */
  async () => {
    const { device_jio_id } = parseQueryString();
    if (device_jio_id) {
      return Promise.resolve(device_jio_id);
    }
    return await getUserId()
  }

export const deleteQuestionIdsFromSessionStorage = questions => {
  questions.forEach(question => {
    if (question && questionId(question) && getObjectFromStorage(questionId(question))) {
      clearFromStorage(questionId(question));
    }
  });
}

export const getComputeRisk = async (language, sourceId, skip_compute_risk, prefer_app_store) => {
  /* istanbul ignore next */
  try {
    const response = await getWorkflow(language, prefer_app_store);
    const { worflow_template: questions, workflow_id } = response;
    const userId = (sourceId === 'JioHealthHub' ? await getUserIdJhh() : await getUserId());
    const workflowResponse = {
      source_user_id: userId,
      workflow_id,
      language: language,
      workflow_responses: responseForPosting(questions)
    };
    const results = await computeRisk(workflowResponse, skip_compute_risk);
    if (results.apiResponseCode === 'success') {
      deleteQuestionIdsFromSessionStorage(questions);
      await fireSurveyResultEvent(language, results, workflowResponse, ess().eventData);
      raiseGAEvent(config.gaEcSymptomChecker, config.gaEaCompleted, sourceId);
      appStorage.removeItem("gaEventScInit");
      return new Promise(resolve => {
        resolve({ results });
      });
    } else return new Promise(resolve => {
      resolve({});
    });
  } catch (error) {
    /* istanbul ignore next */
    return new Promise(resolve => {
      resolve({});
    });
  }
}

